import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasToken } from '../services/authService';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';

const INACTIVE_MEMBERSHIP = new Set(['suspended', 'rejected']);

/**
 * ProtectedRoute - Requires authentication to access protected routes
 * 
 * Checks authentication status via JWT token and redirects to login
 * if not authenticated.
 *
 * Migration note: Replaced `credentials: 'include'` (session cookies)
 * with JWT Bearer token sent via apiFetch.
 */
function ProtectedRoute({ children }) {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [tenantAccessRevoked, setTenantAccessRevoked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Quick local check: if no token at all, skip the API call
    if (!hasToken()) {
      setIsAuthenticated(false);
      setTenantAccessRevoked(false);
      setLoading(false);
      return;
    }

    try {
      const response = await apiFetch('/api/auth/status');
      const data = await response.json().catch(() => ({}));

      if (response.status === 404) {
        console.warn('Auth endpoint not found, redirecting to login');
        setIsAuthenticated(false);
        setTenantAccessRevoked(false);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        console.warn('Auth check not OK:', getUserFacingErrorMessage(
          response.status,
          data,
          `HTTP ${response.status}`
        ));
        setIsAuthenticated(false);
        setTenantAccessRevoked(false);
        return;
      }

      setIsAuthenticated(data.authenticated);
      const st = data.user?.membership_status;
      setTenantAccessRevoked(
        Boolean(st && INACTIVE_MEMBERSHIP.has(String(st).toLowerCase()))
      );
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      setTenantAccessRevoked(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 'calc(100vh - 200px)',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid rgba(0, 170, 255, 0.2)',
          borderTop: '4px solid var(--primary-color)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ 
          color: 'var(--text-color)',
          fontSize: '14px'
        }}>Loading...</div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (
    tenantAccessRevoked &&
    location.pathname !== '/account-inactive'
  ) {
    return <Navigate to="/account-inactive" replace />;
  }

  return children;
}

export default ProtectedRoute;
